import { useState } from 'react';
import { Title, useNotify } from 'react-admin';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import TuneIcon from '@mui/icons-material/Tune';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

import {
  GET_PLAN,
  GET_FEATURE_DEFINITIONS,
  GET_LIMIT_DEFINITIONS,
  SET_PLAN_FEATURE,
  SET_PLAN_LIMIT,
  SET_PLAN_REWARDS,
} from './subscription.queries';
import {
  AdminPlan,
  AdminPlanFeature,
  AdminPlanLimit,
  AdminPlanRewards,
  AdminFeatureDefinition,
  AdminLimitDefinition,
  PLAN_STATUS_LABELS,
  PLAN_STATUS_COLORS,
  CATEGORY_LABELS,
  FEATURE_CATEGORY_LABELS,
  LIMIT_CATEGORY_LABELS,
  formatPrice,
} from './subscription.types';
import { CURRENCY_LABEL } from '@ekaa/tokens/vocabulary';

// ============================================================================
// TAB PANEL
// ============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// ============================================================================
// FEATURE EDITOR DIALOG
// ============================================================================

interface FeatureEditorDialogProps {
  open: boolean;
  planId: string;
  feature: AdminFeatureDefinition | null;
  currentValue?: AdminPlanFeature;
  onClose: () => void;
  onSaved: () => void;
}

function FeatureEditorDialog({
  open,
  planId,
  feature,
  currentValue,
  onClose,
  onSaved,
}: FeatureEditorDialogProps) {
  const notify = useNotify();
  const [setFeature, { loading }] = useMutation(SET_PLAN_FEATURE);
  const [formData, setFormData] = useState({
    isIncluded: currentValue?.isIncluded ?? true,
    value: currentValue?.value || '',
    tierLevel: currentValue?.tierLevel || 1,
    tooltip: currentValue?.tooltip || '',
  });

  // Reset form when dialog opens
  const handleOpen = () => {
    setFormData({
      isIncluded: currentValue?.isIncluded ?? true,
      value: currentValue?.value || '',
      tierLevel: currentValue?.tierLevel || 1,
      tooltip: currentValue?.tooltip || '',
    });
  };

  if (open && feature && formData.isIncluded !== (currentValue?.isIncluded ?? true)) {
    handleOpen();
  }

  const handleSubmit = async () => {
    if (!feature) return;
    try {
      await setFeature({
        variables: {
          planId,
          featureId: feature.id,
          isIncluded: formData.isIncluded,
          value: formData.value || null,
          tierLevel: formData.tierLevel || null,
          tooltip: formData.tooltip || null,
        },
      });
      notify('Feature mise à jour', { type: 'success' });
      onSaved();
      onClose();
    } catch {
      notify('Erreur lors de la mise à jour', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Configurer: {feature?.name}
        <Typography variant="caption" display="block" color="text.secondary">
          {feature?.description}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isIncluded}
                onChange={(e) => setFormData({ ...formData, isIncluded: e.target.checked })}
              />
            }
            label="Inclus dans ce forfait"
          />

          {feature?.valueType === 'NUMBER' && (
            <TextField
              label="Valeur"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              fullWidth
              helperText={feature.unit ? `Unité: ${feature.unit}` : undefined}
            />
          )}

          {feature?.valueType === 'TIER' && (
            <FormControl fullWidth>
              <InputLabel>Niveau</InputLabel>
              <Select
                value={formData.tierLevel}
                label="Niveau"
                onChange={(e) => setFormData({ ...formData, tierLevel: Number(e.target.value) })}
              >
                <MenuItem value={1}>Niveau 1 - Basic</MenuItem>
                <MenuItem value={2}>Niveau 2 - Standard</MenuItem>
                <MenuItem value={3}>Niveau 3 - Premium</MenuItem>
                <MenuItem value={4}>Niveau 4 - Enterprise</MenuItem>
              </Select>
            </FormControl>
          )}

          <TextField
            label="Tooltip (info-bulle)"
            value={formData.tooltip}
            onChange={(e) => setFormData({ ...formData, tooltip: e.target.value })}
            fullWidth
            multiline
            rows={2}
            helperText="Texte affiché au survol pour plus d'informations"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// LIMIT EDITOR DIALOG
// ============================================================================

interface LimitEditorDialogProps {
  open: boolean;
  planId: string;
  limit: AdminLimitDefinition | null;
  currentValue?: AdminPlanLimit;
  onClose: () => void;
  onSaved: () => void;
}

function LimitEditorDialog({
  open,
  planId,
  limit,
  currentValue,
  onClose,
  onSaved,
}: LimitEditorDialogProps) {
  const notify = useNotify();
  const [setLimit, { loading }] = useMutation(SET_PLAN_LIMIT);
  const [formData, setFormData] = useState({
    value: currentValue?.value ?? limit?.defaultValue ?? 0,
    isUnlimited: currentValue?.isUnlimited ?? false,
    resetPeriod: currentValue?.resetPeriod || 'monthly',
    tooltip: currentValue?.tooltip || '',
  });

  const handleOpen = () => {
    setFormData({
      value: currentValue?.value ?? limit?.defaultValue ?? 0,
      isUnlimited: currentValue?.isUnlimited ?? false,
      resetPeriod: currentValue?.resetPeriod || 'monthly',
      tooltip: currentValue?.tooltip || '',
    });
  };

  if (open && limit && formData.value !== (currentValue?.value ?? limit?.defaultValue ?? 0)) {
    handleOpen();
  }

  const handleSubmit = async () => {
    if (!limit) return;
    try {
      await setLimit({
        variables: {
          planId,
          limitId: limit.id,
          value: formData.isUnlimited ? 0 : formData.value,
          isUnlimited: formData.isUnlimited,
          resetPeriod: formData.resetPeriod,
          tooltip: formData.tooltip || null,
        },
      });
      notify('Limite mise à jour', { type: 'success' });
      onSaved();
      onClose();
    } catch {
      notify('Erreur lors de la mise à jour', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Configurer: {limit?.name}
        <Typography variant="caption" display="block" color="text.secondary">
          {limit?.description}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isUnlimited}
                onChange={(e) => setFormData({ ...formData, isUnlimited: e.target.checked })}
              />
            }
            label="Illimité"
          />

          {!formData.isUnlimited && (
            <TextField
              label="Valeur limite"
              type="number"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: Number(e.target.value) })}
              fullWidth
              helperText={limit?.unit ? `Unité: ${limit.unit}` : undefined}
            />
          )}

          <FormControl fullWidth>
            <InputLabel>Période de réinitialisation</InputLabel>
            <Select
              value={formData.resetPeriod}
              label="Période de réinitialisation"
              onChange={(e) => setFormData({ ...formData, resetPeriod: e.target.value })}
            >
              <MenuItem value="none">Jamais</MenuItem>
              <MenuItem value="daily">Quotidien</MenuItem>
              <MenuItem value="weekly">Hebdomadaire</MenuItem>
              <MenuItem value="monthly">Mensuel</MenuItem>
              <MenuItem value="yearly">Annuel</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Tooltip (info-bulle)"
            value={formData.tooltip}
            onChange={(e) => setFormData({ ...formData, tooltip: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// REWARDS EDITOR
// ============================================================================

interface RewardsEditorProps {
  planId: string;
  rewards: AdminPlanRewards | null;
  onSaved: () => void;
}

function RewardsEditor({ planId, rewards, onSaved }: RewardsEditorProps) {
  const notify = useNotify();
  const [setRewards, { loading }] = useMutation(SET_PLAN_REWARDS);
  const [formData, setFormData] = useState({
    multiplier: rewards?.multiplier ?? 1,
    cashbackPercent: rewards?.cashbackPercent ?? 0,
    signupBonus: rewards?.signupBonus ?? 0,
    referralBonus: rewards?.referralBonus ?? 0,
    referralPercent: rewards?.referralPercent ?? 0,
    monthlyBonus: rewards?.monthlyBonus ?? 0,
    yearlyBonus: rewards?.yearlyBonus ?? 0,
    exclusiveRewards: rewards?.exclusiveRewards ?? [],
    priorityAccess: rewards?.priorityAccess ?? false,
  });
  const [newReward, setNewReward] = useState('');

  const handleSubmit = async () => {
    try {
      await setRewards({
        variables: {
          planId,
          multiplier: formData.multiplier,
          cashbackPercent: formData.cashbackPercent,
          signupBonus: formData.signupBonus,
          referralBonus: formData.referralBonus,
          referralPercent: formData.referralPercent,
          monthlyBonus: formData.monthlyBonus,
          yearlyBonus: formData.yearlyBonus,
          exclusiveRewards: formData.exclusiveRewards.length > 0 ? formData.exclusiveRewards : null,
          priorityAccess: formData.priorityAccess,
        },
      });
      notify('Rewards mis à jour', { type: 'success' });
      onSaved();
    } catch {
      notify('Erreur lors de la mise à jour', { type: 'error' });
    }
  };

  const addExclusiveReward = () => {
    if (newReward.trim()) {
      setFormData({
        ...formData,
        exclusiveRewards: [...formData.exclusiveRewards, newReward.trim()],
      });
      setNewReward('');
    }
  };

  const removeExclusiveReward = (index: number) => {
    setFormData({
      ...formData,
      exclusiveRewards: formData.exclusiveRewards.filter((_, i) => i !== index),
    });
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CardGiftcardIcon color="primary" />
          Programme de Rewards
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          {/* Points & Cashback */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Points & Cashback
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="Multiplicateur de points"
              type="number"
              value={formData.multiplier}
              onChange={(e) => setFormData({ ...formData, multiplier: Number(e.target.value) })}
              fullWidth
              inputProps={{ step: 0.1, min: 0 }}
              helperText="Ex: 2 = double points"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="Cashback (%)"
              type="number"
              value={formData.cashbackPercent}
              onChange={(e) => setFormData({ ...formData, cashbackPercent: Number(e.target.value) })}
              fullWidth
              inputProps={{ step: 0.5, min: 0, max: 100 }}
            />
          </Grid>

          {/* Bonus */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              Bonus
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label={`Bonus inscription (${CURRENCY_LABEL})`}
              type="number"
              value={formData.signupBonus}
              onChange={(e) => setFormData({ ...formData, signupBonus: Number(e.target.value) })}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label={`Bonus parrainage (${CURRENCY_LABEL})`}
              type="number"
              value={formData.referralBonus}
              onChange={(e) => setFormData({ ...formData, referralBonus: Number(e.target.value) })}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="Commission parrainage (%)"
              type="number"
              value={formData.referralPercent}
              onChange={(e) => setFormData({ ...formData, referralPercent: Number(e.target.value) })}
              fullWidth
              inputProps={{ step: 0.5, min: 0, max: 100 }}
            />
          </Grid>

          {/* Periodic Bonus */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              Bonus Périodiques
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label={`Bonus mensuel (${CURRENCY_LABEL})`}
              type="number"
              value={formData.monthlyBonus}
              onChange={(e) => setFormData({ ...formData, monthlyBonus: Number(e.target.value) })}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label={`Bonus annuel (${CURRENCY_LABEL})`}
              type="number"
              value={formData.yearlyBonus}
              onChange={(e) => setFormData({ ...formData, yearlyBonus: Number(e.target.value) })}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>

          {/* Options */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              Options
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.priorityAccess}
                  onChange={(e) => setFormData({ ...formData, priorityAccess: e.target.checked })}
                />
              }
              label="Accès prioritaire aux nouvelles fonctionnalités"
            />
          </Grid>

          {/* Exclusive Rewards */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              Rewards Exclusifs
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Ajouter un reward exclusif"
                value={newReward}
                onChange={(e) => setNewReward(e.target.value)}
                fullWidth
                size="small"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExclusiveReward())}
              />
              <Button variant="outlined" onClick={addExclusiveReward}>
                Ajouter
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.exclusiveRewards.map((reward, index) => (
                <Chip
                  key={index}
                  label={reward}
                  onDelete={() => removeExclusiveReward(index)}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ mt: 1 }}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer les rewards'}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// FEATURES TAB
// ============================================================================

interface FeaturesTabProps {
  planId: string;
  planFeatures: AdminPlanFeature[];
  allFeatures: AdminFeatureDefinition[];
  onRefresh: () => void;
}

function FeaturesTab({ planId, planFeatures, allFeatures, onRefresh }: FeaturesTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<AdminFeatureDefinition | null>(null);
  const [selectedCurrentValue, setSelectedCurrentValue] = useState<AdminPlanFeature | undefined>();

  const handleEdit = (feature: AdminFeatureDefinition, currentValue?: AdminPlanFeature) => {
    setSelectedFeature(feature);
    setSelectedCurrentValue(currentValue);
    setDialogOpen(true);
  };

  // Group features by category
  const featuresByCategory = allFeatures.reduce((acc, feature) => {
    const cat = feature.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(feature);
    return acc;
  }, {} as Record<string, AdminFeatureDefinition[]>);

  // Map plan features by feature ID for quick lookup
  const planFeatureMap = new Map(planFeatures.map((pf) => [pf.feature.id, pf]));

  return (
    <>
      {Object.entries(featuresByCategory).map(([category, features]) => (
        <Card key={category} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {FEATURE_CATEGORY_LABELS[category] || category}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Feature</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="center">Inclus</TableCell>
                    <TableCell>Valeur</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {features.map((feature) => {
                    const planFeature = planFeatureMap.get(feature.id);
                    return (
                      <TableRow key={feature.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {feature.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {feature.code}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={feature.valueType}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {planFeature?.isIncluded ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <CancelIcon color="disabled" />
                          )}
                        </TableCell>
                        <TableCell>
                          {planFeature?.value || planFeature?.tierLevel
                            ? `${planFeature.value || ''} ${planFeature.tierLevel ? `(Niveau ${planFeature.tierLevel})` : ''}`
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Configurer">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(feature, planFeature)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}

      <FeatureEditorDialog
        open={dialogOpen}
        planId={planId}
        feature={selectedFeature}
        currentValue={selectedCurrentValue}
        onClose={() => setDialogOpen(false)}
        onSaved={onRefresh}
      />
    </>
  );
}

// ============================================================================
// LIMITS TAB
// ============================================================================

interface LimitsTabProps {
  planId: string;
  planLimits: AdminPlanLimit[];
  allLimits: AdminLimitDefinition[];
  onRefresh: () => void;
}

function LimitsTab({ planId, planLimits, allLimits, onRefresh }: LimitsTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLimit, setSelectedLimit] = useState<AdminLimitDefinition | null>(null);
  const [selectedCurrentValue, setSelectedCurrentValue] = useState<AdminPlanLimit | undefined>();

  const handleEdit = (limit: AdminLimitDefinition, currentValue?: AdminPlanLimit) => {
    setSelectedLimit(limit);
    setSelectedCurrentValue(currentValue);
    setDialogOpen(true);
  };

  // Group limits by category
  const limitsByCategory = allLimits.reduce((acc, limit) => {
    const cat = limit.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(limit);
    return acc;
  }, {} as Record<string, AdminLimitDefinition[]>);

  // Map plan limits by limit ID
  const planLimitMap = new Map(planLimits.map((pl) => [pl.limit.id, pl]));

  return (
    <>
      {Object.entries(limitsByCategory).map(([category, limits]) => (
        <Card key={category} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {LIMIT_CATEGORY_LABELS[category] || category}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Limite</TableCell>
                    <TableCell align="center">Valeur</TableCell>
                    <TableCell>Période</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {limits.map((limit) => {
                    const planLimit = planLimitMap.get(limit.id);
                    return (
                      <TableRow key={limit.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {limit.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {limit.code} {limit.unit && `(${limit.unit})`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {planLimit?.isUnlimited ? (
                            <Chip
                              icon={<AllInclusiveIcon />}
                              label="Illimité"
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Typography variant="body2" fontWeight={600}>
                              {planLimit?.value ?? limit.defaultValue ?? '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {planLimit?.resetPeriod || '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Configurer">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(limit, planLimit)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}

      <LimitEditorDialog
        open={dialogOpen}
        planId={planId}
        limit={selectedLimit}
        currentValue={selectedCurrentValue}
        onClose={() => setDialogOpen(false)}
        onSaved={onRefresh}
      />
    </>
  );
}

// ============================================================================
// PRICING TAB
// ============================================================================

interface PricingTabProps {
  plan: AdminPlan;
}

function PricingTab({ plan }: PricingTabProps) {
  return (
    <Grid container spacing={3}>
      {/* Base Pricing */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MonetizationOnIcon color="primary" />
              Tarification de base
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Prix mensuel</Typography>
                <Typography variant="h6" fontWeight={600}>
                  {formatPrice(plan.monthlyPrice)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Prix annuel</Typography>
                <Typography variant="h6" fontWeight={600}>
                  {plan.yearlyPrice ? formatPrice(plan.yearlyPrice) : '-'}
                </Typography>
              </Box>
              {plan.yearlyDiscount && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Réduction annuelle</Typography>
                  <Chip label={`-${plan.yearlyDiscount}%`} color="success" size="small" />
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Jours d'essai</Typography>
                <Typography fontWeight={500}>{plan.trialDays} jours</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* Multi-currency Pricing */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Prix par devise
            </Typography>
            <Divider sx={{ my: 2 }} />
            {plan.currencies && plan.currencies.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Devise</TableCell>
                      <TableCell align="right">Mensuel</TableCell>
                      <TableCell align="right">Annuel</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plan.currencies.map((curr) => (
                      <TableRow key={curr.currencyCode}>
                        <TableCell>
                          <Chip label={curr.currencyCode} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          {curr.currencySymbol} {curr.monthlyPrice.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {curr.yearlyPrice
                            ? `${curr.currencySymbol} ${curr.yearlyPrice.toLocaleString()}`
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                Aucune tarification multi-devises configurée
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PlanDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  // Queries
  const { data, loading, error, refetch } = useQuery(GET_PLAN, {
    variables: { id },
    skip: !id,
  });
  const { data: featuresData } = useQuery(GET_FEATURE_DEFINITIONS);
  const { data: limitsData } = useQuery(GET_LIMIT_DEFINITIONS);

  const plan: AdminPlan | null = data?.adminPlan;
  const allFeatures: AdminFeatureDefinition[] = featuresData?.adminFeatureDefinitions || [];
  const allLimits: AdminLimitDefinition[] = limitsData?.adminLimitDefinitions || [];

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error || !plan) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error?.message || 'Forfait non trouvé'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/plans')} sx={{ mt: 2 }}>
          Retour aux forfaits
        </Button>
      </Box>
    );
  }

  const statusColor = PLAN_STATUS_COLORS[plan.status];

  return (
    <Box sx={{ p: 3 }}>
      <Title title={`Forfait: ${plan.name}`} />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/plans')}
            sx={{ mb: 2 }}
          >
            Retour aux forfaits
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 8,
                height: 60,
                borderRadius: 1,
                backgroundColor: plan.colorPrimary || '#6366F1',
              }}
            />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4" fontWeight={700}>
                  {plan.name}
                </Typography>
                {plan.isPopular && (
                  <Chip
                    icon={<StarIcon />}
                    label="Populaire"
                    color="warning"
                    size="small"
                  />
                )}
              </Box>
              <Typography variant="body1" color="text.secondary">
                Code: {plan.code} • {CATEGORY_LABELS[plan.category]}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={PLAN_STATUS_LABELS[plan.status]}
            sx={{
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              fontWeight: 600,
            }}
          />
        </Box>
      </Box>

      {/* Description & Highlights */}
      {(plan.description || (plan.highlights && plan.highlights.length > 0)) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {plan.description && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                {plan.description}
              </Typography>
            )}
            {plan.highlights && plan.highlights.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Points forts
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {plan.highlights.map((highlight, idx) => (
                    <Chip
                      key={idx}
                      icon={<CheckCircleIcon />}
                      label={highlight}
                      variant="outlined"
                      color="success"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<MonetizationOnIcon />} label="Tarification" iconPosition="start" />
          <Tab icon={<TuneIcon />} label="Features" iconPosition="start" />
          <Tab icon={<TuneIcon />} label="Limites" iconPosition="start" />
          <Tab icon={<CardGiftcardIcon />} label="Rewards" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <PricingTab plan={plan} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <FeaturesTab
          planId={plan.id}
          planFeatures={plan.features || []}
          allFeatures={allFeatures}
          onRefresh={refetch}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <LimitsTab
          planId={plan.id}
          planLimits={plan.limits || []}
          allLimits={allLimits}
          onRefresh={refetch}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <RewardsEditor
          planId={plan.id}
          rewards={plan.rewards || null}
          onSaved={refetch}
        />
      </TabPanel>
    </Box>
  );
}

export default PlanDetail;
